import React, { useState, useContext, useMemo } from "react";
import {
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Snackbar,
  Alert,
  Fab
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";

import axios from "axios";
import { OrderContext } from "../../contexts/OrderContext";
import searchIcon from "../../assets/img/icons/search.png"; // Update if needed

const CreateMetalType = () => {
  const { storeData, fetchStoreData } = useContext(OrderContext);
  const { metaltypes = [] } = storeData;

  const [showForm, setShowForm] = useState(false);
  const [metalName, setMetalName] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [searchTerm, setSearchTerm] = useState("");

  const filteredMetals = useMemo(() => {
    return metaltypes.filter((metal) =>
      metal.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, metaltypes]);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const mfr_id = localStorage.getItem("id");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/fetchorder/CreateMetalType`, {
        name: metalName,
        mfr_id
      });
      fetchStoreData();
      setSnackbar({ open: true, message: "Metal type created successfully!", severity: "success" });
      setMetalName("");
      setShowForm(false);
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || "Error creating metal type", severity: "error" });
    }
    setLoading(false);
  };

  return (
    <Box p={3} position="relative">
      {/* List & Search */}
      {!showForm && (
        <Box maxWidth={600} mx="auto">
          {/* Search */}
          <div className="custom-search-box">
            <img src={searchIcon} alt="Search" className="search-icon" />
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Metal List */}
          <Grid container spacing={2} mb={6}>
            {filteredMetals.map((metal) => (
              <Grid item xs={12} key={metal.id}>
                <Card sx={{ p: 1, boxShadow: 1 }}>
                  <Typography fontSize="14px" fontWeight="bold">{metal.name}</Typography>
                </Card>
              </Grid>
            ))}
            {filteredMetals.length === 0 && (
              <Typography textAlign="center" color="gray" mt={2}>No metal types found</Typography>
            )}
          </Grid>
        </Box>
      )}

      {/* Create Form */}
      {showForm && (
        <Card>
          <CardContent>
            <Typography variant="h5" fontWeight="bold" textAlign="center" mb={2} color="#0D7377">
              Create Metal Type
            </Typography>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Metal Type Name"
                    value={metalName}
                    onChange={(e) => setMetalName(e.target.value)}
                    required
                    sx={{
                      '& label': { color: '#64748B' },
                      '& label.Mui-focused': { color: '#0D7377' },
                      '& .MuiInputBase-input': { color: '#333' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#A0ABBB' },
                        '&:hover fieldset': { borderColor: '#A0ABBB' },
                        '&.Mui-focused fieldset': { borderColor: '#0D7377' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    sx={{
                      backgroundColor: '#0D7377',
                      color: '#fff',
                      '&:hover': { backgroundColor: '#0A5C60' },
                    }}
                  >
                    {loading ? "Saving..." : "Save"}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Floating Toggle Button */}
      <Fab
        color="primary"
        onClick={() => setShowForm(!showForm)}
        sx={{
          position: "fixed",
          bottom: 80,
          right: 24,
          bgcolor: "#0D7377",
          '&:hover': { bgcolor: "#0A5C60" },
        }}
      >
        {showForm ? <CloseIcon /> : <AddIcon />}
      </Fab>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{
          '&.MuiSnackbar-root': {
            bottom: '100px !important', // force override snackbar position
          }
        }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateMetalType;
